import {
  ConsignmentApiModel,
  ConsignmentParcelItemApiModel,
  Dimensions,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { tinyId } from '../../../../lib/helpers/uid';
import { QuantitySummary } from '../QuantitySummary';
import { QuantityValueSummary } from '../QuantityValueSummary';
import { ConsolidateParcelQuantities, SplitConsignmentWeight } from './GetConsignmentFromItem';

export function GenerateConsignmentFromItem(
  item: SmartSendItemApiModel,
  dimensions: Dimensions,
  requestedParcels: number
) {
  return item.MarketplaceOrderLines.length === 1
    ? SingleOrder(item, dimensions, requestedParcels)
    : MultiOrderMagically(item, dimensions, requestedParcels);
}

function MultiOrderMagically(item: SmartSendItemApiModel, dimensions: Dimensions, requestedParcels: number) {
  if (item.MarketplaceOrderLines.length == 1) {
    throw 'Error';
  }

  const c: ConsignmentApiModel = { Parcels: [] };

  // Split all orders into an item per quantity
  const splitItems: ConsignmentParcelItemApiModel[] = [];
  item.MarketplaceOrderLines.forEach((m) => {
    const valSum = QuantityValueSummary(m.Value, m.Quantity);
    for (let index = 0; index < m.Quantity; index++) {
      splitItems.push({
        Description: m.Product.Description,
        Id: m.Id,
        UID: tinyId(),
        Quantity: 1,
        Value: index == 0 ? valSum.valueForFirstItem : valSum.valueForOtherItems
      });
    }
  });

  // Realistically we can't create more parcels than products as we dont know when item to split
  const actualParcels = Math.min(requestedParcels, splitItems.length);

  // Parcel Per Request
  for (let index = 0; index < actualParcels; index++) {
    c.Parcels.push({ Contents: [], Dimensions: { ...dimensions }, UID: tinyId(), Id: item.Id, CanAdd: true });
  }

  // Figure out how many items we should allocate for each parcel
  const summary = QuantitySummary(splitItems.length, actualParcels);

  let orderIndex = 0;
  for (let pindex = 0; pindex < c.Parcels.length; pindex++) {
    for (let index = 0; index < summary[pindex]; index++) {
      const mpo = splitItems[orderIndex];
      c.Parcels[pindex].Contents.push(mpo);
      orderIndex++;
    }
  }

  // For each parcel, group all the orders with the same MarketplaceOrderLine.Id back together
  ConsolidateParcelQuantities(c);

  SplitConsignmentWeight(dimensions, c);

  return c;
}

function SingleOrder(item: SmartSendItemApiModel, dimensions: Dimensions, parcelsToCreate: number) {
  if (item.MarketplaceOrderLines.length > 1) {
    throw 'Error';
  }

  const c: ConsignmentApiModel = { Parcels: [] };
  const mainOl = item.MarketplaceOrderLines[0];
  const summary = QuantityValueSummary(mainOl.Value, parcelsToCreate);

  // Create Parcels
  for (let index = 0; index < parcelsToCreate; index++) {
    c.Parcels.push({
      Contents: [],
      Dimensions: { ...dimensions },
      UID: tinyId(),
      Id: item.Id,
      CanAdd: true
    });
  }

  // Allocate the items
  if (mainOl.Quantity > parcelsToCreate) {
    // More Items Than Parcels, split equally
    const pcs = QuantitySummary(mainOl.Quantity, parcelsToCreate);
    const summary = QuantityValueSummary(mainOl.Value, mainOl.Quantity);
    let overallIndex = 0;
    for (let parcelIndex = 0; parcelIndex < pcs.length; parcelIndex++) {
      // Create the first Qty
      c.Parcels[parcelIndex].Contents = [
        {
          Description: mainOl.Product.Description,
          Id: mainOl.Id,
          UID: tinyId(),
          Quantity: 1,
          Value: overallIndex === 0 ? summary.valueForFirstItem : summary.valueForOtherItems
        }
      ];
      // Use the remaining to calculate the correct qty and value
      for (let index = 1; index < pcs[parcelIndex]; index++) {
        c.Parcels[parcelIndex].Contents[0].Quantity++;
        c.Parcels[parcelIndex].Contents[0].Value += summary.valueForOtherItems;
        overallIndex++;
      }
    }
  } else {
    // Pad out, one item per parcel
    for (let index = 0; index < parcelsToCreate; index++) {
      c.Parcels[index].Contents.push({
        Description: mainOl.Product.Description,
        Id: mainOl.Id,
        UID: tinyId(),
        Quantity: 1,
        Value: index === 0 ? summary.valueForFirstItem : summary.valueForOtherItems
      });
    }
  }

  SplitConsignmentWeight(dimensions, c);

  return c;
}
